import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["dialog", "form"];

  initialize() {
    this.previousValues = {};
  }

  /**
   * Closes all the `dialog` targets, and opens the dialog with the given id.
   *
   * Stores the form data right before opening the dialog, so it can be put
   * back in the form when the dialog is closed discarding changes.
   */
  showDialog({ params }) {
    this.dialogTargets.forEach((dialog) => {
      if (dialog.id === params.dialogId) {
        this.storePreviousValues(dialog);
        dialog.showModal();
      } else {
        dialog.close();
      }
    });
  }

  /**
   * Closes all the `dialog` targets, discarding their data, bringing back the
   * data stored in `this.previousValues` for any dialog that was open.
   *
   * Dispatches the `dialog-discarded` event to signal that all the input
   * values are back to their previous state for the dialog that was discarded.
   */
  discardDialogs() {
    this.dialogTargets.forEach((dialog) => {
      if (dialog.open) {
        this.restorePreviousValues(dialog);
        dialog.close();
        this.dispatch("dialog-discarded", { target: dialog });
      }
    });
  }

  /**
   * Checks if an element is a restorable element:
   *  - is inside the dialog
   *  - has a name
   *  - has an id
   *  - is not a button
   *  - is not a fieldset
   *  - is not a submit button
   */
  // eslint-disable-next-line class-methods-use-this
  isRestorableDialogInput(dialog, element) {
    return (
      dialog.contains(element) &&
      !!element.name &&
      !!element.id &&
      element.type !== "button" &&
      element.type !== "fieldset" &&
      element.type !== "submit"
    );
  }

  /**
   * For a given dialog, finds all of the form's input elements in the dialog,
   * excluding buttons and none-input elements like fieldsets, storing the
   * current input value, or checked state in the case of checkbox and radio
   * inputs, in `this.previousValues` that can later be used to restore them.
   */
  storePreviousValues(dialog) {
    [...this.formTarget.elements].forEach((element) => {
      if (this.isRestorableDialogInput(dialog, element)) {
        let value;
        if (element.type === "checkbox" || element.type === "radio") {
          value = element.checked;
        } else {
          value = element.value;
        }
        // eslint-disable-next-line no-param-reassign
        this.previousValues[element.id] = value;
      }
    });
  }

  /**
   * For a given dialog, finds all of the form's input elements in the dialog,
   * excluding buttons and none-input elements like fieldsets, and restores the
   * value/checked state from `this.previousValues`.
   *
   * Dispatches a `previous-value-restored` event to notify elements that may
   * need to do more work when their value changes programmatically.
   * Inputs can subscribe to this event to make more adjustments.
   */
  restorePreviousValues(dialog) {
    [...this.formTarget.elements].forEach((element) => {
      if (this.isRestorableDialogInput(dialog, element)) {
        const value = this.previousValues[element.id];
        if (value !== undefined) {
          // Enables elements that may have been disabled by a validate
          // requesrt before restoring. This is especiall true for fields that
          // `assessment_kind` affects.
          // eslint-disable-next-line no-param-reassign
          element.disabled = false;
          if (element.type === "checkbox" || element.type === "radio") {
            // eslint-disable-next-line no-param-reassign
            element.checked = value;
          } else {
            // eslint-disable-next-line no-param-reassign
            element.value = value;
          }
          // Dispatches a `previous-value-restored` to notify elements that may
          // need to do more work when their value changes programmatically.
          this.dispatch("previous-value-restored", { target: element });
          // Clears the used previous value.
          this.previousValues[element.id] = undefined;
        }
      }
    });
  }
}
